import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  removeProfileAvatar,
  uploadProfileAvatar,
} from "@/lib/profile/media";
import {
  isProfileNameTitle,
  isProfileOccupation,
  isProfileSalutation,
  type OwnProfile,
  type ProfileUpdateInput,
  type ProfileVisibility,
} from "@/lib/profile/types";

type ProfileRow = {
  id: string;
  full_name: string;
  last_name: string | null;
  first_name: string | null;
  salutation: string | null;
  name_title: string | null;
  professional_title: string | null;
  occupation: string | null;
  phone: string | null;
  city: string | null;
  bio: string | null;
  registry_number: string | null;
  avatar_path: string | null;
  account_status: OwnProfile["accountStatus"];
  profile_visibility?: ProfileVisibility | null;
};

export async function getMyProfile(): Promise<OwnProfile | null> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    return null;
  }

  const { data, error } = await supabase.rpc("get_my_profile");

  if (error) {
    throw error;
  }

  if (!data) {
    return {
      id: user.id,
      email: user.email ?? null,
      fullName: user.email ?? "Felhasználó",
      lastName: null,
      firstName: null,
      salutation: null,
      nameTitle: null,
      professionalTitle: null,
      occupation: null,
      phone: null,
      city: null,
      bio: null,
      registryNumber: null,
      avatarPath: null,
      accountStatus: "pending",
      profileVisibility: "public",
    };
  }

  const row = data as ProfileRow;
  const legacyNameParts = row.full_name.trim().split(/\s+/);
  const fallbackLastName = legacyNameParts.shift() ?? "";
  const fallbackFirstName = legacyNameParts.join(" ");
  return {
    id: row.id,
    email: user.email ?? null,
    fullName: row.full_name,
    lastName: row.last_name ?? fallbackLastName,
    firstName: row.first_name ?? fallbackFirstName,
    salutation: isProfileSalutation(row.salutation) ? row.salutation : null,
    nameTitle: isProfileNameTitle(row.name_title) ? row.name_title : null,
    professionalTitle: row.professional_title,
    occupation: isProfileOccupation(row.occupation) ? row.occupation : null,
    phone: row.phone,
    city: row.city,
    bio: row.bio,
    registryNumber: row.registry_number,
    avatarPath: row.avatar_path,
    accountStatus: row.account_status,
    profileVisibility:
      row.profile_visibility === "private" ? "private" : "public",
  };
}

export async function updateMyProfile(input: ProfileUpdateInput): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    throw new Error("A profil szerkesztéséhez be kell jelentkeznie.");
  }

  const { error } = await supabase
    .from("profiles")
    .update({
      last_name: input.lastName.trim(),
      first_name: input.firstName.trim(),
      full_name: `${input.lastName.trim()} ${input.firstName.trim()}`,
      salutation: input.salutation,
      name_title: input.nameTitle || null,
      occupation: input.occupation,
      professional_title: input.professionalTitle.trim() || null,
      phone: input.phone.trim() || null,
      city: input.city.trim() || null,
      bio: input.bio.trim() || null,
      registry_number: input.registryNumber.trim() || null,
      profile_visibility: input.profileVisibility,
      introduction_settings_completed: true,
    })
    .eq("id", user.id);

  if (error) {
    throw error;
  }
}

export async function updateMyAvatar(file: File): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    throw new Error("A profilkép feltöltéséhez be kell jelentkeznie.");
  }

  const { data: current, error: currentError } = await supabase
    .from("profiles")
    .select("avatar_path")
    .eq("id", user.id)
    .maybeSingle();

  if (currentError) {
    throw currentError;
  }

  const previousPath =
    typeof current?.avatar_path === "string" ? current.avatar_path : null;
  const path = await uploadProfileAvatar(user.id, file);
  const { error } = await supabase
    .from("profiles")
    .update({ avatar_path: path })
    .eq("id", user.id);

  if (error) {
    await removeProfileAvatar(path).catch(() => undefined);
    throw error;
  }

  if (previousPath && previousPath !== path) {
    await removeProfileAvatar(previousPath).catch(() => undefined);
  }
}

export async function removeMyAvatar(): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    throw new Error("A profilkép törléséhez be kell jelentkeznie.");
  }

  const { data: current, error: currentError } = await supabase
    .from("profiles")
    .select("avatar_path")
    .eq("id", user.id)
    .maybeSingle();

  if (currentError) {
    throw currentError;
  }

  const previousPath =
    typeof current?.avatar_path === "string" ? current.avatar_path : null;
  const { error } = await supabase
    .from("profiles")
    .update({ avatar_path: null })
    .eq("id", user.id);

  if (error) {
    throw error;
  }

  await removeProfileAvatar(previousPath).catch(() => undefined);
}

export async function completeFirstUseTour(): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    throw new Error("A bemutató befejezéséhez be kell jelentkeznie.");
  }

  const { error } = await supabase
    .from("profiles")
    .update({ onboarding_completed: true })
    .eq("id", user.id);

  if (error) {
    throw error;
  }
}
